import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api";
import Logo from "../components/Logo";
import ProfileMenu from "../components/ProfileMenu";
import Pagination from "../components/Pagination";

const REPORTS_PER_PAGE = 10;
import {
  Plus, AlertTriangle, ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, Gem,
  CalendarDays, Search, BarChart3, Target, Shield, UserSearch,
  Lock, FileBarChart, ClipboardList, Globe, FileText, FileSpreadsheet,
  FileType, Zap, ArrowRight, Loader2, RefreshCw, TrendingUp, Wrench, LayoutGrid,
} from "lucide-react";

// Risk -> Aurora severity color
const RC = (s) => s >= 70 ? "var(--au-error)" : s >= 40 ? "var(--au-warn)" : "var(--au-accent-ink)";
const SC = { done:"var(--au-success)", running:"var(--au-warn)", queued:"var(--au-info)", failed:"var(--au-error)", DONE:"var(--au-success)", RUNNING:"var(--au-warn)", QUEUED:"var(--au-info)", FAILED:"var(--au-error)" };

// Severity counts from scans
const getSeverity = (scans) => {
  let crit=0, high=0, med=0, low=0;
  scans.forEach(s => {
    if (s.risk_score == null) return;
    if (s.risk_score >= 70) crit++;
    else if (s.risk_score >= 50) high++;
    else if (s.risk_score >= 30) med++;
    else low++;
  });
  return { crit, high, med, low };
};

// Last 7 days scan count
const getLast7Days = (scans) => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const dayKey = d.toISOString().split('T')[0];
    const count = scans.filter(s => s.created_at?.startsWith(dayKey)).length;
    days.push({ label: d.toLocaleDateString('en', {weekday:'short'}), count, isToday: i === 0 });
  }
  return days;
};

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [scans, setScans] = useState([]);
  const [scanPage, setScanPage] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [showKycPopup, setShowKycPopup] = useState(false);
  const [resetCountdown, setResetCountdown] = useState("");
  const nav = useNavigate();

  useEffect(() => { load(); }, []);

  // Quota reset countdown - reflects the new per-week / per-month period
  useEffect(() => {
    if (!user?.daily_scans_reset_at) return;
    // New model: free = weekly, paid tiers = monthly. Fall back to weekly span.
    const periodHours = user?.quota_period === "month" ? 24 * 30
      : user?.quota_period === "week" ? 24 * 7
      : 24 * 7;
    const update = () => {
      const reset = new Date(user.daily_scans_reset_at);
      reset.setHours(reset.getHours() + periodHours);
      const diff = reset - new Date();
      if (diff <= 0) { setResetCountdown("resetting..."); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setResetCountdown(d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`);
    };
    update();
    const i = setInterval(update, 60000);
    return () => clearInterval(i);
  }, [user]);

  const load = async () => {
    setError(false); setBusy(true);
    try {
      const [u, s] = await Promise.all([api.get("/api/auth/me"), api.get("/api/scan/history")]);
      setUser(u.data);
      setScans(s.data);
      if (!u.data.is_verified && !u.data.is_admin && !sessionStorage.getItem("kyc_popup_dismissed")) {
        setTimeout(() => setShowKycPopup(true), 800);
      }
    } catch (e) {
      setError(true);
      toast.error("Couldn't load your dashboard");
    } finally { setBusy(false); }
  };

  const download = async (id, fmt) => {
    try {
      const res = await api.get(`/api/reports/${fmt}/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.download = `aegis_report_${id}.${fmt === "pdf-standard" ? "pdf" : fmt}`; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (busy) return <div className="au-root" style={{display:"flex",flexDirection:"column",gap:12,alignItems:"center",justifyContent:"center",color:"var(--au-accent-ink)",fontFamily:"var(--au-font-mono)"}}><Loader2 size={28} className="au-spin"/><span>Loading…</span></div>;

  if (error) return (
    <div className="au-root" style={{display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div className="au-glass au-pad-lg" style={{textAlign:"center",maxWidth:420}}>
        <AlertTriangle size={40} style={{color:"var(--au-warn)",marginBottom:14}}/>
        <h2 style={{fontSize:18,fontWeight:700,color:"var(--au-text)",marginBottom:8}}>Couldn't load your dashboard</h2>
        <p style={{color:"var(--au-muted)",fontSize:13,marginBottom:18}}>Check your connection and try again.</p>
        <button onClick={load} className="au-pill au-pill-primary au-pill-sm"><RefreshCw size={14}/> Retry</button>
      </div>
    </div>
  );

  const planClr = { free:"var(--au-muted)", FREE:"var(--au-muted)", pay_scan:"var(--au-info)", PAY_SCAN:"var(--au-info)", starter:"var(--au-success)", STARTER:"var(--au-success)", pro:"var(--au-accent-ink)", PRO:"var(--au-accent-ink)", agency:"var(--au-warn)", AGENCY:"var(--au-warn)" };
  const sev = getSeverity(scans);
  const last7 = getLast7Days(scans);
  const max7 = Math.max(...last7.map(d => d.count), 1);
  const completedScans = scans.filter(s => s.risk_score != null);
  // A user with no completed scans has NO safety score. Defaulting avgRisk to 0
  // used to yield 100/100 + a green "Secure" shield for someone who had never
  // scanned anything — a false all-clear, and the worst thing a security
  // product can tell a new user. Absence of data is now shown as absence.
  const hasScore = completedScans.length > 0;
  const avgRisk = hasScore ? Math.round(completedScans.reduce((sum, s) => sum + s.risk_score, 0) / completedScans.length) : null;
  const safetyScore = hasScore ? 100 - avgRisk : null;
  const gaugeColor = !hasScore ? "var(--au-muted)"
    : safetyScore >= 70 ? "var(--au-success)" : safetyScore >= 40 ? "var(--au-warn)" : "var(--au-error)";
  const SafetyIcon = !hasScore ? ShieldQuestion
    : safetyScore >= 70 ? ShieldCheck : safetyScore >= 40 ? ShieldAlert : ShieldX;
  const safetyLabel = !hasScore ? "Not assessed"
    : safetyScore >= 70 ? "Secure" : safetyScore >= 40 ? "Moderate" : "At Risk";
  const isFirstRun = scans.length === 0;

  return (
    <div className="au-root">
      <nav style={S.nav}>
        <Link to="/" style={{textDecoration:"none"}}><Logo size="small" /></Link>
        <div style={{display:"flex",alignItems:"center",gap:18}}>
          <Link to="/scan"><button className="au-pill au-pill-glass au-pill-sm"><Plus size={16}/> New Scan</button></Link>
          <ProfileMenu />
        </div>
      </nav>

      <div style={{maxWidth:1200,margin:"0 auto",padding:"32px 24px"}}>
        <h1 style={{fontSize:26,fontWeight:700,color:"var(--au-text)",letterSpacing:1.5,marginBottom:6}}>
          {isFirstRun ? "Welcome" : "Welcome back"}, {user?.name || "User"}
        </h1>
        <p style={{color:"var(--au-muted)",marginBottom:28,fontSize:14}}>{isFirstRun ? "Let’s run your first scan — it takes under a minute." : "Your security command center"}</p>

        {!user?.is_verified && (
          <div className="au-glass" style={{padding:"16px 22px",marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:"var(--au-warn)",display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={16}/> Verify Your Account</div>
              <div style={{fontSize:12,color:"var(--au-text-2)",marginTop:4}}>Complete KYC to unlock full features</div>
            </div>
            <Link to="/kyc"><button className="au-pill au-pill-primary au-pill-sm">Verify Now <ArrowRight size={14}/></button></Link>
          </div>
        )}

        {/* FIRST RUN: the only thing a user with no data should be asked to do.
            The analytics below are meaningful only once scans exist, so the
            charts are suppressed until then rather than rendering all-zero. */}
        {isFirstRun && (
          <div className="au-glass au-pad-lg" style={{marginBottom:24,display:"flex",alignItems:"center",justifyContent:"space-between",gap:20,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:16,minWidth:260}}>
              <span className="au-badge au-badge-dark" style={{flex:"none"}}><Shield size={22}/></span>
              <div>
                <div style={{fontSize:"var(--au-fs-h3)",fontWeight:700,color:"var(--au-text)"}}>Run your first scan</div>
                <div style={{fontSize:"var(--au-fs-body)",color:"var(--au-text-2)",marginTop:4,maxWidth:"46ch",lineHeight:1.6}}>
                  Enter a domain you own. Aegis checks SSL/TLS, DNS, headers, ports and CVEs, and returns a report in under a minute.
                </div>
              </div>
            </div>
            <Link to="/scan"><button className="au-pill au-pill-primary">Start Free Scan <ArrowRight size={16}/></button></Link>
          </div>
        )}

        {/* HERO: Security Score Gauge + Stats */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:18,marginBottom:24}}>
          <div className="au-glass au-pad" style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div className="au-stat-lbl" style={{letterSpacing:3,marginBottom:14}}>Safety Score</div>
            <svg width="180" height="180" viewBox="0 0 180 180">
              <circle cx="90" cy="90" r="70" fill="none" stroke="var(--au-glass-strong)" strokeWidth="14"/>
              <circle cx="90" cy="90" r="70" fill="none" stroke={gaugeColor} strokeWidth="14" strokeLinecap="round"
                strokeDasharray={hasScore ? `${(safetyScore/100)*440} 440` : "0 440"} transform="rotate(-90 90 90)"
                style={{transition:"stroke-dasharray 1s ease-out"}}/>
              <text x="90" y="95" textAnchor="middle" fill="var(--au-text)" fontSize="36" fontWeight="700" fontFamily="JetBrains Mono">{hasScore ? safetyScore : "—"}</text>
              <text x="90" y="115" textAnchor="middle" fill="var(--au-muted)" fontSize="10" letterSpacing="2">/ 100</text>
            </svg>
            <div style={{marginTop:8,fontSize:13,color:gaugeColor,fontWeight:700,display:"flex",alignItems:"center",gap:6}}><SafetyIcon size={16}/> {safetyLabel}</div>
            <div style={{fontSize:10,color:"var(--au-muted)",marginTop:4}}>{hasScore ? `Avg of ${completedScans.length} scans` : "Run a scan to get your score"}</div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <StatBig label="Plan" value={(user?.plan || "FREE").toUpperCase()} color={planClr[user?.plan] || "var(--au-muted)"} icon={Gem} sub={user?.plan === 'free' ? "Upgrade for more" : "Active"}/>
            <StatBig label={user?.quota_period === "month" ? "Scans This Month" : "Scans This Week"} value={`${user?.daily_used || 0}/${user?.daily_limit || 1}`} color="var(--au-success)" icon={CalendarDays} sub={resetCountdown ? `Resets in ${resetCountdown}` : "—"}/>
            <StatBig label="Total Scans" value={scans.length} color="var(--au-accent-ink)" icon={Search} sub={`${completedScans.length} completed`}/>
            <StatBig label="High Risk" value={sev.crit + sev.high} color="var(--au-error)" icon={AlertTriangle} sub={`${sev.crit} critical, ${sev.high} high`}/>
          </div>
        </div>

        {/* CHARTS ROW — hidden on a first run; an empty chart teaches nothing */}
        {!isFirstRun && (
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:18,marginBottom:24}}>
          <div className="au-glass au-pad">
            <div className="au-stat-lbl" style={{letterSpacing:3,marginBottom:18,display:"flex",alignItems:"center",gap:8}}><BarChart3 size={14} style={{color:"var(--au-accent-ink)"}}/> Last 7 Days</div>
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",height:120,gap:8}}>
              {last7.map((d, i) => (
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                  <div className="au-mono" style={{fontSize:10,color:"var(--au-text-2)"}}>{d.count}</div>
                  <div style={{width:"100%",background:d.isToday ? "var(--au-accent)" : "var(--au-glass-strong)",borderRadius:4,height:`${(d.count/max7)*90}px`,minHeight:4,transition:"height 0.8s"}}/>
                  <div style={{fontSize:10,color:d.isToday ? "var(--au-accent-ink)" : "var(--au-muted)",fontWeight:d.isToday?700:400}}>{d.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="au-glass au-pad">
            <div className="au-stat-lbl" style={{letterSpacing:3,marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Target size={14} style={{color:"var(--au-accent-ink)"}}/> Risk Distribution</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <RiskBar label="Critical" count={sev.crit} total={completedScans.length} color="var(--au-error)"/>
              <RiskBar label="High" count={sev.high} total={completedScans.length} color="var(--au-warn)"/>
              <RiskBar label="Medium" count={sev.med} total={completedScans.length} color="var(--au-accent-ink)"/>
              <RiskBar label="Low" count={sev.low} total={completedScans.length} color="var(--au-success)"/>
            </div>
          </div>
        </div>
        )}

        {/* BILLING / SUBSCRIPTION */}
        <BillingCard user={user} />

        {/* QUICK ACTIONS */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:24}}>
          <ActionCard to="/scan" icon={Shield} label="New Scan"/>
          <ActionCard to="/activity" icon={LayoutGrid} label="All Activity"/>
          <ActionCard to="/trends" icon={TrendingUp} label="Trends & Compare"/>
          <ActionCard to="/osint" icon={UserSearch} label="OSINT"/>
        </div>

        {/* SCAN HISTORY */}
        <div className="au-glass" style={{overflow:"hidden",marginBottom:18}}>
          <div style={S.sectionHead}>
            <span style={{fontSize:14,fontWeight:700,color:"var(--au-text)",letterSpacing:1,display:"flex",alignItems:"center",gap:8}}><ClipboardList size={16} style={{color:"var(--au-accent-ink)"}}/> SCAN HISTORY</span>
            <Link to="/scan" style={{color:"var(--au-accent-ink)",fontSize:12,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4}}><Plus size={13}/> New Scan</Link>
          </div>

          {scans.length === 0 ? (
            <div style={{padding:48,textAlign:"center"}}>
              <Shield size={42} style={{color:"var(--au-accent-ink)",marginBottom:14}}/>
              <p style={{color:"var(--au-muted)",fontSize:15,marginBottom:14}}>No scans yet. Start your first!</p>
              <Link to="/scan"><button className="au-pill au-pill-primary">Start Scan <ArrowRight size={16}/></button></Link>
            </div>
          ) : (
            <div>
              <div style={{...S.row, ...S.rowHead}}>
                <span>Target</span><span>Status</span><span>Risk</span><span>Date</span><span>Actions</span>
              </div>
              {scans.slice(scanPage * REPORTS_PER_PAGE, scanPage * REPORTS_PER_PAGE + REPORTS_PER_PAGE).map(scan => (
                <div key={scan.id} style={S.row}>
                  <span className="au-mono" style={{fontSize:13,color:"var(--au-text-2)",display:"flex",alignItems:"center",gap:8}}>
                    {scan.risk_score != null && <span style={{width:8,height:8,borderRadius:"50%",background:RC(scan.risk_score)}}/>}
                    {scan.target}
                  </span>
                  <span>
                    <span style={{color:SC[scan.status] || "var(--au-muted)",border:`1px solid ${SC[scan.status] || "var(--au-muted)"}`,padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700,letterSpacing:1}}>
                      {scan.status?.toString().toUpperCase()}
                    </span>
                  </span>
                  <span className="au-mono" style={{fontSize:18,fontWeight:700,color:scan.risk_score!=null?RC(scan.risk_score):"var(--au-muted)"}}>
                    {scan.risk_score!=null?scan.risk_score:"—"}
                  </span>
                  <span style={{fontSize:12,color:"var(--au-muted)",display:"flex",flexDirection:"column",gap:2}}>
                    <span>{new Date(scan.created_at).toLocaleDateString()}</span>
                    <span style={{fontSize:11,opacity:0.72}}>{new Date(scan.created_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</span>
                  </span>
                  <span style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {(scan.status === "done" || scan.status === "DONE") && ["pdf","pdf-standard","csv","docx","html","json"].map(fmt => (
                      <button key={fmt} onClick={async () => {
                        if (fmt === "html") {
                          try {
                            const r = await api.get(`/api/reports/html/${scan.id}`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
                            const w = window.open(url, "_blank");
                            if (!w) toast.error("Popup blocked — allow popups to view the HTML report");
                            setTimeout(() => URL.revokeObjectURL(url), 30000);
                          } catch (e) { console.error(e); toast.error("Failed to load HTML report"); }
                        } else {
                          download(scan.id, fmt);
                        }
                      }} className="au-pill au-pill-glass au-pill-sm" style={{padding:"4px 10px",fontSize:10,fontWeight:700}}>
                        {fmt === "html"
                          ? <><Globe size={12}/> HTML</>
                          : fmt === "pdf" ? <><FileText size={12}/> PDF</>
                          : fmt === "pdf-standard" ? <><FileText size={12}/> PDF (Paged)</>
                          : fmt === "csv" ? <><FileSpreadsheet size={12}/> CSV</>
                          : fmt === "json" ? <><FileType size={12}/> JSON</>
                          : <><FileType size={12}/> DOCX</>}
                      </button>
                    ))}
                  </span>
                </div>
              ))}
              <Pagination page={scanPage} setPage={setScanPage} total={scans.length} pageSize={REPORTS_PER_PAGE} />
            </div>
          )}
        </div>

        {showKycPopup && (
          <div onClick={(e) => e.target === e.currentTarget && (sessionStorage.setItem("kyc_popup_dismissed","1"), setShowKycPopup(false))}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div className="au-glass au-pad-lg" style={{maxWidth:500,width:"100%",textAlign:"center"}}>
              <div style={{display:"flex",justifyContent:"center",marginBottom:14}}><Lock size={48} style={{color:"var(--au-accent-ink)"}}/></div>
              <h2 style={{fontSize:24,fontWeight:700,color:"var(--au-text)",marginBottom:10,letterSpacing:1}}>Verify Your Account</h2>
              <p style={{color:"var(--au-text-2)",fontSize:14,lineHeight:1.7,marginBottom:8}}>Welcome <strong style={{color:"var(--au-accent-ink)"}}>{user?.name}</strong>!</p>
              <p style={{color:"var(--au-muted)",fontSize:14,lineHeight:1.7,marginBottom:24}}>Complete <strong style={{color:"var(--au-warn)"}}>KYC</strong> to unlock scanning features.</p>
              <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                <button onClick={() => { sessionStorage.setItem("kyc_popup_dismissed","1"); setShowKycPopup(false); }} className="au-pill au-pill-ghost au-pill-sm">Skip</button>
                <Link to="/kyc" style={{textDecoration:"none"}}>
                  <button onClick={() => setShowKycPopup(false)} className="au-pill au-pill-primary au-pill-sm">Verify Now <ArrowRight size={14}/></button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {(user?.plan === "agency" || user?.plan === "AGENCY") && !user?.is_sub_user && (
          <div className="au-glass" style={{padding:"18px 24px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:"var(--au-warn)",marginBottom:4,display:"flex",alignItems:"center",gap:6}}><Zap size={15}/> Agency Team</div>
              <div style={{color:"var(--au-text-2)",fontSize:13}}>Manage your team members — invite, remove, monitor activity</div>
            </div>
            <Link to="/team" style={{textDecoration:"none"}}><button className="au-pill au-pill-primary au-pill-sm">Manage Team <ArrowRight size={14}/></button></Link>
          </div>
        )}
        {(user?.plan === "free" || user?.plan === "FREE") && (
          <div className="au-glass" style={{padding:"18px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:"var(--au-text)"}}>Upgrade to Starter — $25/mo</div>
              <div style={{fontSize:13,color:"var(--au-muted)",marginTop:4}}>7 scans/mo · full findings + remediation code · PDF + HTML + DOCX + CSV</div>
            </div>
            <Link to="/wallet" className="au-pill au-pill-primary au-pill-sm" style={{textDecoration:"none"}}>Upgrade <ArrowRight size={14}/></Link>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBig({ label, value, color, icon: Icon, sub }) {
  return (
    <div className="au-glass" style={{padding:"16px 20px"}}>
      <div className="au-stat-lbl" style={{letterSpacing:2,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>{label}</span><Icon size={16} style={{color:"var(--au-accent-ink)"}}/>
      </div>
      <div className="au-stat-num au-mono" style={{fontSize:24,fontWeight:700,color}}>{value}</div>
      {sub && <div style={{fontSize:10,color:"var(--au-muted)",marginTop:4}}>{sub}</div>}
    </div>
  );
}

function RiskBar({ label, count, total, color }) {
  const pct = total ? (count/total)*100 : 0;
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
        <span style={{color:"var(--au-text-2)"}}>{label}</span>
        <span className="au-mono" style={{color,fontWeight:700}}>{count}</span>
      </div>
      <div style={{height:6,background:"var(--au-glass-strong)",borderRadius:3,overflow:"hidden"}}>
        <div style={{width:`${pct}%`,height:"100%",background:color,transition:"width 0.8s"}}/>
      </div>
    </div>
  );
}

function ActionCard({ to, icon: Icon, label }) {
  return (
    <Link to={to} style={{textDecoration:"none"}}>
      <div className="au-glass au-lift" style={{padding:"20px",textAlign:"center",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
        <span className="au-badge"><Icon size={24}/></span>
        <div style={{fontSize:13,color:"var(--au-text)",fontWeight:700,letterSpacing:1}}>{label}</div>
      </div>
    </Link>
  );
}

function BillingCard({ user }) {
  if (!user) return null;
  const plan = String(user.plan || "free").replace(/^plantype\./i, "");
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  const status = user.subscription_status;
  const active = status === "active";
  const cancelling = status === "cancelling";
  const renews = user.subscription_renews_at ? new Date(user.subscription_renews_at) : null;
  const renewsStr = renews && !isNaN(renews.getTime())
    ? renews.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;
  const sub = active && renewsStr ? `Renews ${renewsStr}`
    : cancelling && renewsStr ? `Access until ${renewsStr}`
    : "Pay-as-you-go · no active subscription";
  return (
    <div className="au-glass au-pad" style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span className="au-badge"><Gem size={20} /></span>
        <div>
          <div className="au-stat-lbl" style={{ letterSpacing: 2 }}>Your Plan</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "var(--au-text)", display: "flex", alignItems: "center", gap: 8 }}>
            {planLabel}
            {active && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "var(--au-success)", background: "rgba(31,208,163,0.12)", border: "1px solid var(--au-accent)", borderRadius: 20, padding: "2px 8px" }}>ACTIVE</span>}
            {cancelling && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "var(--au-warn)", border: "1px solid var(--au-warn)", borderRadius: 20, padding: "2px 8px" }}>CANCELLING</span>}
          </div>
          <div style={{ fontSize: 12, color: "var(--au-muted)", marginTop: 2 }}>{sub}</div>
        </div>
      </div>
      <Link to="/wallet" className="au-pill au-pill-primary au-pill-sm" style={{ textDecoration: "none" }}>
        {active || cancelling ? "Manage billing" : "Upgrade"} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

const S = {
  nav: {padding:"14px 28px",display:"flex",alignItems:"center",justifyContent:"space-between"},
  sectionHead: {padding:"16px 22px",borderBottom:"1px solid var(--au-hair)",display:"flex",justifyContent:"space-between",alignItems:"center"},
  row: {display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1.5fr",padding:"14px 22px",borderBottom:"1px solid var(--au-hair)",alignItems:"center"},
  rowHead: {padding:"10px 22px",fontSize:10,color:"var(--au-muted)",letterSpacing:2,textTransform:"uppercase",borderBottom:"1px solid var(--au-hair)"},
};
